import type { BoundObjectRef } from '@wix/editor-platform-feature-store';
import type {
  ElementFilter,
  SelectFilterParams,
} from '@wix/public-editor-platform-interfaces';
import type { SelectFilterPanelOptions } from '@wix/editor-platform-host-integration-apis';

import { PlatformPrivateServices } from '../../../PlatformPrivateServices';

export type ISelectFilterParams = SelectFilterParams;

export class HostFilterSelectorAPI {
  constructor(private ps: PlatformPrivateServices) {}

  async selectFilter(
    dataItemKey: string,
    context: {
      target: BoundObjectRef;
      providerIdentifier?: string;
      onChange: SelectFilterPanelOptions['onChange'];
    },
  ): Promise<ElementFilter | undefined> {
    const panelOptions: SelectFilterPanelOptions = {
      filterDataItemKey: dataItemKey,
      onChange: context.onChange,
      ...(context.providerIdentifier
        ? { providerIdentifier: context.providerIdentifier }
        : {}),
    };

    // TODO: Add explicit-options mode when `SelectFilterParams` supports
    // passing value and callbacks. In that mode, the panel should only manage
    // UI state and return the selected filter to the consumer without SDK
    // persistence.
    if (this.ps.hostAPI.editor.selectFilter) {
      return this.ps.hostAPI.editor.selectFilter(context.target, panelOptions);
    }

    const wqlFilterModalUIService =
      this.ps.platformPackagesBridge.WqlFilterModalUIServiceAPI;

    if (wqlFilterModalUIService) {
      return new Promise<ElementFilter | undefined>((resolve) => {
        const editorPointer = context.target.editorPointer as Parameters<
          typeof wqlFilterModalUIService.openPanel
        >[0]['editorPointer'];

        const openPanelProps = {
          editorPointer,
          onChange: async (nextValue: ElementFilter) => {
            const updatedValue = await panelOptions.onChange(nextValue);
            resolve(updatedValue);
            return updatedValue;
          },
          ...(panelOptions.providerIdentifier
            ? { providerIdentifier: panelOptions.providerIdentifier }
            : {}),
          filterDataItemKey: panelOptions.filterDataItemKey,
        };

        wqlFilterModalUIService.openPanel(openPanelProps);
      });
    }

    throw new Error('selectFilter panel is not supported');
  }
}
